// -----------------------------------------------------------------------------
// 📦 File: Billing.jsx
// -----------------------------------------------------------------------------
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import backend from "../../../../../api";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatAmount(value) {
  if (typeof value !== "number") return "$0";
  return `$${value.toFixed(2)}`;
}

// util
const money = (n) => `$${(+n).toFixed(0)}`;

// normalize any legacy keys coming from backend
const normalizePlanKey = (key) => {
  if (!key) return "free";
  switch (key) {
    case "basic":
      return "individual";
    case "premium":
      return "studio";
    default:
      return key; // 'free', 'individual', 'studio', etc.
  }
};

// treat these statuses as "has an active sub" for disabling UI
const ACTIVE_STATUSES = ["active", "trialing", "past_due", "unpaid", "incomplete"];

export default function Billing() {
  const { user } = useSelector((s) => s.auth);
  const navigate = useNavigate();

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState("");

  // pricing state
  const [billing, setBilling] = useState(
    () => user?.activeSubscription?.plan?.billingCycle || "monthly"
  ); // 'monthly' | 'yearly'
  const isYearly = billing === "yearly";

  // derive current plan from user
  const rawPlanKey =
    user?.activeSubscription?.plan?.key ||
    user?.subscription ||
    "free";

  const currentPlanKey = normalizePlanKey(rawPlanKey);

  const subscriptionStatus =
    user?.activeSubscription?.status ||
    (currentPlanKey !== "free" ? "active" : "none");

  const hasActiveSubscription = ACTIVE_STATUSES.includes(subscriptionStatus);

  const plans = [
    {
      key: "individual",
      title: "Individual",
      subtitle: "Best for freelancers",
      monthly: 29,
      features: [
        "Unlimited Downloads",
        "Saved Collections",
        "Advanced Filters",
        "Manufacturer Visibility",
      ],
    },
    {
      key: "studio",
      title: "Studio",
      subtitle: "Best for large teams & studios",
      monthly: 49,
      highlight: true,
      features: [
        "Shared Project Libraries",
        "Team Seat Management",
        "Priority Support",
        "Early Access to New Features",
      ],
    },
  ];

  const priceLabel = (p) => {
    const perMonth = isYearly ? p.monthly * 0.85 : p.monthly;
    return `${money(perMonth)}/month`;
  };

  const billedNote = () =>
    isYearly ? "Billed Annually · Save 15%" : "Billed Monthly";

  // load invoices
  useEffect(() => {
    if (!user || !user._id) {
      setLoading(false);
      return;
    }

    (async () => {
      try {
        // pass userId in the route: /me/:userId
        const res = await backend.get(
          `/api/subscription-invoices/me/${user._id}`
        );
        const list = res?.data?.data || [];
        setInvoices(list);
      } catch (err) {
        console.error(
          "load subscription invoices:",
          err?.response?.data || err.message
        );
        setErrMsg(
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          "Failed to load subscription invoices."
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  const handleDownload = (invoice) => {
    try {
      const doc = new jsPDF();

      const planLabel =
        invoice.planKey === "studio"
          ? "Studio"
          : invoice.planKey === "individual"
          ? "Individual"
          : invoice.planKey;

      doc.setFontSize(16);
      doc.text("LighTrack Subscription Invoice", 14, 18);

      doc.setFontSize(11);
      doc.text(`Invoice ID: ${invoice._id}`, 14, 26);
      doc.text(`Plan: ${planLabel}`, 14, 33);
      doc.text(`Billing Cycle: ${invoice.billingCycle}`, 14, 40);
      doc.text(`Status: ${invoice.status}`, 14, 47);
      doc.text(`Issued At: ${formatDate(invoice.issuedAt)}`, 14, 54);
      if (invoice.paidAt) {
        doc.text(`Paid At: ${formatDate(invoice.paidAt)}`, 14, 61);
      }

      const startY = invoice.paidAt ? 70 : 64;

      const head = [["Description", "Qty", "Unit", "Amount"]];
      const body = (invoice.lineItems || []).map((li) => [
        li.description || "",
        li.quantity != null ? String(li.quantity) : "1",
        li.unitAmount != null ? formatAmount(li.unitAmount) : "$0.00",
        li.amount != null ? formatAmount(li.amount) : "$0.00",
      ]);

      autoTable(doc, {
        head,
        body,
        startY,
      });

      const finalY = doc.lastAutoTable
        ? doc.lastAutoTable.finalY || startY
        : startY + 10;
      doc.setFontSize(12);
      doc.text(
        `Total: ${formatAmount(invoice.amount)} ${invoice.currency || "usd"}`,
        14,
        finalY + 10
      );

      doc.save(`LighTrack_Subscription_Invoice_${invoice._id}.pdf`);
    } catch (e) {
      console.error("download invoice pdf:", e);
      alert("Could not generate PDF. Please try again.");
    }
  };

  const handleChangePlan = async (plan) => {
    // If not logged in (shouldn't happen here, but just in case)
    if (!user || !user._id) {
      navigate("/signup/customer", {
        state: {
          fromPricing: true,
          planKey: plan.key,
          billingCycle: billing,
        },
      });
      return;
    }

    try {
      const body = {
        planKey: plan.key,          // 'individual' | 'studio'
        billingCycle: billing,      // 'monthly' | 'yearly'
        userId: user._id,
        companyId: user.companyId || null,
        type: "subscription",
      };

      const res = await backend.post(
        "/api/stripe/create-checkout-session",
        body
      );
      const url = res?.data?.url;
      if (!url) {
        alert("Could not start checkout. Please try again.");
        return;
      }

      window.location.href = url;
    } catch (err) {
      console.error("checkout error:", err?.response?.data || err.message);
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "Something went wrong while starting checkout.";
      alert(msg);
    }
  };

  if (!user || !user._id) {
    return (
      <div>
        <h2>Subscription & Billing</h2>
        <p className="placeholderText">
          Please sign in to view your subscription and invoices.
        </p>
      </div>
    );
  }

  const currentPlanLabel =
    currentPlanKey === "studio"
      ? "Studio"
      : currentPlanKey === "individual"
      ? "Individual"
      : "Free";

  return (
    <div>
      {/* ======= Current Plan + Upgrade Section ======= */}
      <div
        style={{
          marginBottom: 32,
          padding: 20,
          borderRadius: 16,
          border: "1px solid #e5e7eb",
          background: "#f9fafb",
        }}
      >
        <h2 style={{ margin: 0, marginBottom: 4 }}>Subscription & Billing</h2>
        <p
          style={{
            margin: 0,
            marginBottom: 12,
            fontSize: 13,
            color: "#6b7280",
          }}
        >
          Manage your LighTrack plan, billing cycle, and view payment history.
        </p>

        {/* current plan badge */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 10,
            marginBottom: 16,
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "4px 10px",
              borderRadius: 999,
              background: "rgba(37,99,235,0.08)",
              color: "#1d4ed8",
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background:
                  subscriptionStatus === "active" || subscriptionStatus === "trialing"
                    ? "#22c55e"
                    : "#9ca3af",
              }}
            />
            Current Plan: {currentPlanLabel}
          </div>
          <span
            style={{
              fontSize: 12,
              color: "#6b7280",
            }}
          >
            Status: {subscriptionStatus || "none"}
          </span>
        </div>

        {/* Billing toggle */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 12,
            marginBottom: 18,
          }}
        >
          <div
            style={{
              position: "relative",
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              width: 260,
              background: "#ffffff",
              border: "1px solid #eef0f4",
              borderRadius: 999,
              padding: 4,
              boxShadow: "0 8px 22px rgba(15,23,42,0.06)",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 4,
                left: billing === "monthly" ? 4 : "calc(50% + 4px)",
                width: "calc(50% - 8px)",
                height: "calc(100% - 8px)",
                borderRadius: 999,
                background: "#ffd84d",
                boxShadow: "0 12px 26px rgba(255,216,77,0.35)",
                transition: "left 0.18s ease-out",
              }}
            />
            <button
              type="button"
              onClick={() => setBilling("monthly")}
              style={{
                position: "relative",
                zIndex: 1,
                border: 0,
                background: "transparent",
                color: billing === "monthly" ? "#111827" : "#0f172a",
                fontWeight: 800,
                fontSize: 13,
                padding: "9px 0",
                borderRadius: 999,
                cursor: "pointer",
              }}
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => setBilling("yearly")}
              style={{
                position: "relative",
                zIndex: 1,
                border: 0,
                background: "transparent",
                color: billing === "yearly" ? "#111827" : "#0f172a",
                fontWeight: 800,
                fontSize: 13,
                padding: "9px 0",
                borderRadius: 999,
                cursor: "pointer",
              }}
            >
              Yearly
            </button>
          </div>
          <span style={{ fontSize: 12.5, color: "#7a8593" }}>
            {billedNote()}
          </span>
        </div>

        {/* plan cards (simple version of Pricing cards) */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))",
            gap: 18,
          }}
        >
          {plans.map((p) => {
            const isCurrentPlan =
              hasActiveSubscription && currentPlanKey === p.key;

            const isHigherPlan =
              currentPlanKey === "individual" && p.key === "studio";

            const buttonLabel = isCurrentPlan
              ? "Current Plan"
              : currentPlanKey === "free"
              ? "Upgrade"
              : isHigherPlan
              ? "Upgrade"
              : "Change Plan";

            const cardBackground =
              p.highlight
                ? "linear-gradient(180deg, #10b3a2 0%, #04a28f 100%)"
                : "#ffffff";
            const textColor = p.highlight ? "#ffffff" : "#111827";
            const subTextColor = p.highlight ? "rgba(255,255,255,0.9)" : "#6b7280";

            return (
              <div
                key={p.key}
                style={{
                  borderRadius: 18,
                  padding: 18,
                  border: "1px solid #eef0f4",
                  background: cardBackground,
                  color: textColor,
                  boxShadow: "0 10px 26px rgba(15,23,42,0.08)",
                }}
              >
                <div style={{ marginBottom: 6 }}>
                  <div
                    style={{
                      fontWeight: 800,
                      fontSize: 18,
                      marginBottom: 2,
                    }}
                  >
                    {p.title}
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      color: subTextColor,
                    }}
                  >
                    {p.subtitle}
                  </div>

                  {isCurrentPlan && (
                    <div
                      style={{
                        marginTop: 6,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        padding: "3px 9px",
                        borderRadius: 999,
                        fontSize: 11,
                        fontWeight: 600,
                        background: "rgba(34,197,94,0.12)",
                        color: "#ecfdf3",
                      }}
                    >
                      <span
                        style={{
                          width: 7,
                          height: 7,
                          borderRadius: "50%",
                          background: "#22c55e",
                        }}
                      />
                      Current plan
                    </div>
                  )}
                </div>

                <div style={{ marginTop: 12, marginBottom: 6, fontWeight: 900 }}>
                  {priceLabel(p)}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    marginBottom: 10,
                    color: subTextColor,
                  }}
                >
                  {billedNote()}
                </div>

                <button
                  type="button"
                  onClick={() => !isCurrentPlan && handleChangePlan(p)}
                  disabled={isCurrentPlan}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: "1px solid rgba(226,232,240,0.9)",
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: isCurrentPlan ? "not-allowed" : "pointer",
                    background: isCurrentPlan
                      ? "rgba(148,163,184,0.4)"
                      : p.highlight
                      ? "#ffffff"
                      : "#f4f7fb",
                    color: p.highlight && !isCurrentPlan ? "#0d2942" : "#0d2942",
                    opacity: isCurrentPlan ? 0.7 : 1,
                  }}
                >
                  {buttonLabel}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* ======= Invoice History ======= */}
      {loading && (
        <p className="placeholderText">Loading subscription invoices…</p>
      )}
      {!loading && errMsg && (
        <p style={{ color: "crimson", marginBottom: 16 }}>{errMsg}</p>
      )}

      {!loading && !errMsg && invoices.length === 0 && (
        <p className="placeholderText">No subscription invoices found yet.</p>
      )}

      {!loading && !errMsg && invoices.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              minWidth: "720px",
            }}
          >
            <thead>
              <tr>
                <th style={thStyle}>Plan</th>
                <th style={thStyle}>Billing</th>
                <th style={thStyle}>Amount</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Issued</th>
                <th style={thStyle}>Paid</th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => {
                const planLabel =
                  inv.planKey === "studio"
                    ? "Studio"
                    : inv.planKey === "individual"
                    ? "Individual"
                    : inv.planKey;

                return (
                  <tr key={inv._id}>
                    <td style={tdStyle}>{planLabel}</td>
                    <td style={tdStyle}>{inv.billingCycle}</td>
                    <td style={tdStyle}>{formatAmount(inv.amount)}</td>
                    <td style={tdStyle}>{inv.status}</td>
                    <td style={tdStyle}>{formatDate(inv.issuedAt)}</td>
                    <td style={tdStyle}>{formatDate(inv.paidAt)}</td>
                    <td style={tdStyle}>
                      <button
                        type="button"
                        onClick={() => handleDownload(inv)}
                        style={downloadBtnStyle}
                      >
                        Download PDF
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const thStyle = {
  textAlign: "left",
  padding: "10px 8px",
  borderBottom: "1px solid #e5e7eb",
  fontSize: 13,
  fontWeight: 600,
  color: "#4b5563",
};

const tdStyle = {
  padding: "10px 8px",
  borderBottom: "1px solid #f3f4f6",
  fontSize: 13,
  color: "#111827",
};

const downloadBtnStyle = {
  padding: "6px 10px",
  borderRadius: 6,
  border: "1px solid #0b2545",
  background: "#0b2545",
  color: "#fff",
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
};
